import React, { useState, useEffect, useCallback, useMemo, useRef, ReactNode, memo } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from "../context/ThemeContext";
import { usePolling } from "../context/PollingContext";
import { CRITERIA_API, AWARDS_API, fetchWithAuth } from "../utils/api";
import { useUniversalTextSizing, getUniversalTextConfig } from "../utils/cardTitleSizing";
import { CardTitle, BodyText, SmallText, CaptionText, UniversalBadge, useUniversalSizing } from "./UniversalText";
import {
  Check,
  Clock,
  AlertCircle,
  RefreshCw,
  Users,
  Award,
  TrendingUp,
  BarChart2,
  MessageCircle,
  User,
  Zap,
  Target,
  Calculator,
  Info,
  Edit3,
  Sparkles,
  UserCheck,
  ShieldAlert,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { FlexibleScoreDisplay } from "./FlexibleScoreDisplay";
import ScoreEditModal from "./ScoreEditModal";
import StyleFeedbackEditorModal from "./StyleFeedbackEditorModal";
import { Criterion as ScoringCriterion } from "./ScoringCard";

// Import shared types
import { FinalizedScore as SharedFinalizedScore } from "../types/judge";

// Type definitions
export interface Judge {
  id: string;
  name?: string;
  photoUrl?: string;
  photo?: string;
  color?: string;
  isTeacherCritique?: boolean; // Flag to indicate if this judge is a Teacher Critique judge
}

export interface JudgeProfiles {
  [judgeId: string]: Judge | string;
}

export interface Criterion {
  id: string;
  name?: string;
  label?: string;
  weight?: number;
  maxPoints?: number;
}

export interface JudgeScoreData {
  scores: { [criterionId: string]: number };
  comments?: string;
  isSubmitted?: boolean;
  timestamp?: Date;
  simpleScore?: number;
  iChoreographedThis?: boolean; // Use backend field name
  notFamilyFriendly?: boolean; // Add notFamilyFriendly flag
  styleFeedback?: {
    good?: Array<{category: string, criteria: string}>;
    'needs-work'?: Array<{category: string, criteria: string}>;
  };
}

export interface JudgeScores {
  [judgeId: string]: JudgeScoreData;
}

export interface CriteriaScoreData {
  sum: number;
  count: number;
  average: number;
  total: number;
  weight: number;
}

export interface CriteriaScores {
  [criterionId: string]: CriteriaScoreData;
}

export interface SimpleScores {
  [judgeId: string]: number;
}

export interface ScoreStats {
  criteriaScores: CriteriaScores;
  judgeCount: number;
  simpleScores: SimpleScores;
}

export interface Award {
  name: string;
  color: string;
  minScore: number;
  maxScore: number;
  scaledMin?: number;
  scaledMax?: number;
}

export interface AwardsConfig {
  awards: Award[];
  isPercentageBased: boolean;
  maxPointsPerJudge: number;
  numberOfJudges: number;
}

export interface Routine {
  routineNumber: string;
  name?: string;
  style?: string;
  category?: string;
  styleCategory?: string;
  Category?: string;
  Type?: string;
  type?: string;
  'Dance Style'?: string;
  'Style'?: string;
  [key: string]: any;
}

export interface ContainerDimensions {
  width: number;
  height: number;
}

export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge';
export type ActiveTab = 'scores' | 'comments' | 'stats' | 'final';
export type ScoringMode = 'criteria' | 'simple';

export interface SizeConfig {
  headerSize: string;
  tabTextSize: string;
  tabPadding: string;
  tabIconSize: number;
  scoreSize: string;
  categorySize: string;
  avatarSize: string;
  spacing: string;
  padding: string;
  cardPadding: string;
  gridGap: string;
}

export interface LiveScoreMonitorProps {
  currentRoutine?: Routine | null;
  currentIndex?: number;
  className?: string;
  compact?: boolean;
  currentJudgeProfile?: Judge | null;
  currentJudgeId?: string | null;
  judgeProfiles?: JudgeProfiles;
  containerSize?: ContainerSize;
  enableScoreEditing?: boolean; // New prop to enable/disable score editing for tabulators
  onScoreUpdate?: (judgeId: string, criterionId: string, newScore: number) => void; // Callback for inline score updates
  onCommentUpdate?: (judgeId: string, newComment: string) => void; // Callback for inline comment updates
  onStyleFeedbackUpdate?: (judgeId: string, category: string, criteria: string, feedbackType: 'good' | 'needs-work' | null, batchedData?: any) => void; // Callback for style feedback updates
  onBooleanFlagUpdate?: (judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', newValue: boolean) => void; // Callback for boolean flag updates
  onScoreDelete?: (judgeId: string, routineId: string) => void; // Callback for score deletion
  onChoreographerScoreReplacement?: (judgeId: string) => void; // Callback for replacing choreographer score with average
  styleScoreSheets?: any[]; // Style score sheets for modal
  maxHeight?: string;
  scoringMode?: ScoringMode; // Pass scoring mode from parent to avoid API fetch timing issues
}

export interface CriteriaApiResponse {
  scoringMode?: ScoringMode;
  criteria?: Criterion[];
  isPercentageMode?: boolean;
}

export interface AwardsApiResponse {
  awards?: Award[];
  isPercentageBased?: boolean;
  maxPointsPerJudge?: number;
  numberOfJudges?: number;
}

export interface JudgesUpdateData {
  judges?: string[];
  profiles?: JudgeProfiles;
}

export interface ScoreUpdateData {
  judgeId: string;
  routineId: string;
  scores: { [criterionId: string]: number };
  isSubmitted?: boolean;
  comments?: string;
  simpleScore?: number;
}

export interface RoutineScoresData {
  routineId: string;
  scores: Array<{
    judgeId: string;
    scores: { [criterionId: string]: number };
    comments?: string;
    isSubmitted?: boolean;
    timestamp?: string;
    simpleScore?: number;
  }>;
}

export interface JudgeProfileUpdateData {
  judgeId: string;
  profile: Judge;
}

// Helper function to format scores without unnecessary decimals
const formatScore = (score: number): string => {
  // If the score is a whole number, return it without decimals
  if (score % 1 === 0) {
    return score.toString();
  }
  // Otherwise, return with one decimal place
  return score.toFixed(1);
};

// Generate profile avatar with photo or initials
const getProfileAvatar = (judge: Judge | string, avatarSize: string = 'w-10 h-10'): React.JSX.Element => {
  // If judge is just an ID string (legacy format)
  if (typeof judge === 'string') {
    // Extract initials or just use first character if no name format
    const initial = judge.charAt(0).toUpperCase();
    
    // Generate a deterministic color based on judgeId
    const hue = (judge.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
    const bgColor = `hsl(${hue}, 70%, 30%)`;
    
    // Reduce console logging for performance
    if (process.env.NODE_ENV === 'development') {
      // console.log("Creating avatar for string judge ID:", judge, "with color:", bgColor);
    }
    
    return (
      <div className={`${avatarSize} rounded-full flex items-center justify-center text-white font-bold text-sm relative`} style={{ backgroundColor: bgColor }}>
        {initial}
      </div>
    );
  }
  
  // If we have a judge object with profile information
  if (judge && typeof judge === 'object') {
    // If we have a photo URL, use it
    if (judge.photoUrl || judge.photo) {
      const photoUrl = judge.photoUrl || judge.photo;
      
      return (
        <div className={`${avatarSize} rounded-full bg-gray-300 overflow-hidden relative`}>
          <img 
            src={photoUrl}
            alt={judge.name || "Judge"} 
            className="w-full h-full object-cover"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              // console.log("Judge profile image failed to load:", photoUrl);
              
              const target = e.target as HTMLImageElement;
              if (!target) return;
              
              target.onerror = null;
              target.style.display = 'none';
              
              // Generate a color based on name or id
              const id = judge.id || judge.name || "Judge";
              const hue = (id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
              const bgColor = judge.color || `hsl(${hue}, 70%, 30%)`;
              
              // Get initial from name
              const initial = (judge.name || "J").charAt(0).toUpperCase();
              
              // Create fallback element with initial
              const parent = target.parentNode as HTMLElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = 'w-full h-full rounded-full flex items-center justify-center text-white font-bold text-sm';
                fallback.style.backgroundColor = bgColor;
                fallback.textContent = initial;
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      );
    }
    
    // If no photo but we have a name, use initials
    const initial = judge.name ? judge.name.charAt(0).toUpperCase() : "J";
    
    // Generate a color based on name or id
    const id = judge.id || judge.name || "Judge";
    const hue = (id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
    const bgColor = judge.color || `hsl(${hue}, 70%, 30%)`;
    
    // console.log("Creating initial avatar for judge without photo:", judge.name, "with color:", bgColor);
    
    return (
      <div className={`${avatarSize} rounded-full flex items-center justify-center text-white font-bold text-sm relative`} style={{ backgroundColor: bgColor }}>
        {initial}
      </div>
    );
  }
  
  // Default fallback
  // console.log("Creating default avatar for unknown judge");
  
  return (
    <div className={`${avatarSize} rounded-full flex items-center justify-center text-white font-bold text-sm relative`} style={{ backgroundColor: "hsl(215, 70%, 30%)" }}>
      J
    </div>
  );
};

// Get judge display name
const getJudgeDisplayName = (judge: Judge | string): string => {
  if (typeof judge === 'string') {
    return `Judge ${judge}`;
  }
  
  if (judge && typeof judge === 'object') {
    return judge.name || `Judge ${judge.id || ""}`;
  }
  
  return "Judge";
};

// Get judge first name only
const getJudgeFirstName = (judge: Judge | string): string => {
  if (typeof judge === 'string') {
    return judge;
  }
  
  if (judge && typeof judge === 'object' && judge.name) {
    // Split by space and return first part
    const nameParts = judge.name.trim().split(/\s+/);
    return nameParts[0];
  }
  
  if (judge && typeof judge === 'object' && judge.id) {
    return judge.id;
  }
  
  return "Judge";
};

// Hook to detect container size for LiveScoreMonitor
const useContainerSize = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const [containerSize, setContainerSize] = useState<ContainerSize>('medium');
  const [dimensions, setDimensions] = useState<ContainerDimensions>({ width: 0, height: 0 });

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const { width, height } = rect;
        
        setDimensions({ width, height });
        
        // Determine size based on container dimensions, not viewport
        if (width < 300 || height < 400) {
          setContainerSize('small');
        } else if (width < 500 || height < 600) {
          setContainerSize('medium');
        } else if (width < 800 || height < 800) {
          setContainerSize('large');
        } else {
          setContainerSize('xlarge');
        }
      }
    };

    // Use ResizeObserver for better performance than window resize
    const resizeObserver = new ResizeObserver(updateContainerSize);
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateContainerSize(); // Initial measurement
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return { containerSize, dimensions };
};

const LiveScoreMonitor: React.FC<LiveScoreMonitorProps> = ({
  currentRoutine = null,
  currentIndex = 0,
  className = "",
  compact = false,
  scoringMode: propScoringMode,
  currentJudgeProfile = null,
  currentJudgeId = null,
  judgeProfiles = {},
  containerSize = 'medium', // Allow parent to override container size
  enableScoreEditing = false, // Default to false for safety
  onScoreUpdate,
  onCommentUpdate,
  onStyleFeedbackUpdate,
  onBooleanFlagUpdate,
  onScoreDelete,
  onChoreographerScoreReplacement,
  styleScoreSheets = [],
  maxHeight
}) => {
  // Custom breathing animation styles
  const breathingStyles = `
    @keyframes breathing {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.05); }
    }
    .animate-breathing {
      animation: breathing 2s ease-in-out infinite;
    }
  `;
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerSize: detectedSize, dimensions } = useContainerSize(containerRef);
  
  // Use provided containerSize or detected size
  const effectiveContainerSize = containerSize !== 'medium' ? containerSize : detectedSize;
  
  // Use universal text sizing for consistency across all cards
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);

  // Container-responsive sizing configuration using universal text sizing
  const sizeConfig = useMemo((): SizeConfig => {
    const configs: Record<ContainerSize, SizeConfig> = {
      small: {
        headerSize: universalConfig.smallText,
        tabTextSize: universalConfig.captionText,
        tabPadding: universalConfig.buttonPadding,
        tabIconSize: 10,
        scoreSize: universalConfig.bodyText,
        categorySize: universalConfig.smallText,
        avatarSize: 'w-6 h-6',
        spacing: 'space-y-1',
        padding: universalConfig.sectionPadding,
        cardPadding: universalConfig.sectionPadding,
        gridGap: universalConfig.spacing
      },
      medium: {
        headerSize: universalConfig.bodyText,
        tabTextSize: universalConfig.smallText,
        tabPadding: universalConfig.buttonPadding,
        tabIconSize: 12,
        scoreSize: universalConfig.bodyText,
        categorySize: universalConfig.bodyText,
        avatarSize: 'w-8 h-8',
        spacing: 'space-y-2',
        padding: universalConfig.cardPadding,
        cardPadding: universalConfig.cardPadding,
        gridGap: universalConfig.spacing
      },
      large: {
        headerSize: universalConfig.bodyText,
        tabTextSize: universalConfig.bodyText,
        tabPadding: universalConfig.buttonPadding,
        tabIconSize: 14,
        scoreSize: universalConfig.bodyText,
        categorySize: universalConfig.bodyText,
        avatarSize: 'w-10 h-10',
        spacing: 'space-y-3',
        padding: universalConfig.cardPadding,
        cardPadding: universalConfig.cardPadding,
        gridGap: universalConfig.spacingLarge
      },
      xlarge: {
        headerSize: universalConfig.bodyText,
        tabTextSize: universalConfig.bodyText,
        tabPadding: universalConfig.buttonPadding,
        tabIconSize: 16,
        scoreSize: universalConfig.bodyText,
        categorySize: universalConfig.bodyText,
        avatarSize: 'w-12 h-12',
        spacing: 'space-y-4',
        padding: universalConfig.cardPadding,
        cardPadding: universalConfig.cardPadding,
        gridGap: universalConfig.spacingLarge
      }
    };
    return configs[effectiveContainerSize] || configs.medium;
  }, [effectiveContainerSize, universalConfig]);

  // Dynamic compact mode - use explicit compact prop or detect based on container size
  const isCompactMode = compact || effectiveContainerSize === 'small';
  const { connected, competitionCode } = usePolling();

  const [activeTab, setActiveTab] = useState<ActiveTab>("scores");
  const [judgeScores, setJudgeScores] = useState<JudgeScores>({});
  const [connectedJudges, setConnectedJudges] = useState<string[]>([]);
  const [internalJudgeProfiles, setInternalJudgeProfiles] = useState<JudgeProfiles>({});
  const [judgePositions, setJudgePositions] = useState<Record<string, number>>({});
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [criteriaLoading, setCriteriaLoading] = useState<boolean>(true);
  const [criteriaError, setCriteriaError] = useState<string | null>(null);
  const [scoringMode, setScoringMode] = useState<ScoringMode>(propScoringMode || 'simple'); // 'simple' or 'criteria' - default to simple
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [finalizedScore, setFinalizedScore] = useState<SharedFinalizedScore | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  
  // Inline editing state
  const [editingScore, setEditingScore] = useState<{ judgeId: string; criterionId: string } | null>(null);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [tempScoreValue, setTempScoreValue] = useState<string>("");
  const [tempCommentValue, setTempCommentValue] = useState<string>("");
  
  // Loading states for inline editing
  const [updatingScores, setUpdatingScores] = useState<Set<string>>(new Set());
  const [updatingComments, setUpdatingComments] = useState<Set<string>>(new Set());
  const [updatingStyleFeedback, setUpdatingStyleFeedback] = useState<Set<string>>(new Set());
  const [updatingBooleanFlags, setUpdatingBooleanFlags] = useState<Set<string>>(new Set());
  
  // Reset confirmation state - tracks which judge is in reset confirmation mode
  const [resetConfirmation, setResetConfirmation] = useState<Set<string>>(new Set());
  
  // Style feedback modal state
  const [isStyleFeedbackModalOpen, setIsStyleFeedbackModalOpen] = useState(false);
  const [initialJudgeIndex, setInitialJudgeIndex] = useState(0);
  
  // Awards configuration state
  const [awardsConfig, setAwardsConfig] = useState<AwardsConfig | null>(null);
  const [awardsLoading, setAwardsLoading] = useState<boolean>(true);
  
  // Use a combined judgeProfiles object that prioritizes the props over internal state
  const combinedJudgeProfiles = useMemo((): JudgeProfiles => {
    return { ...internalJudgeProfiles, ...judgeProfiles };
  }, [internalJudgeProfiles, judgeProfiles]);
  
  // Sort judges by their assigned positions
  const sortedConnectedJudges = useMemo(() => {
    // Ensure connectedJudges is an array
    if (!connectedJudges || !Array.isArray(connectedJudges) || connectedJudges.length === 0) {
      console.warn('LiveScoreMonitor - connectedJudges is not a valid array:', {
        connectedJudges,
        type: typeof connectedJudges,
        isArray: Array.isArray(connectedJudges)
      });
      return [];
    }
    
    // Create array of judges with their positions
    const judgesWithPositions = connectedJudges.map(judgeId => ({
      judgeId,
      position: judgePositions[judgeId] || 999 // Use 999 as fallback for judges without assigned positions
    }));
    
    // Sort by position (ascending order: 1, 2, 3, 4, etc.)
    judgesWithPositions.sort((a, b) => a.position - b.position);
    
    // Return just the sorted judge IDs
    const sorted = judgesWithPositions.map(judge => judge.judgeId);
    
    // Debug log for judge sorting (commented out to reduce console noise)
    // console.log('Judge sorting:', {
    //   original: connectedJudges,
    //   positions: judgePositions,
    //   sorted: sorted,
    //   judgesWithPositions
    // });
    
    return sorted;
  }, [connectedJudges, judgePositions]);
  
  // Log when judge profiles change
  useEffect(() => {
    // console.log("Combined judge profiles updated:", combinedJudgeProfiles);
  }, [combinedJudgeProfiles]);
  
  // Add the current judge's profile to judgeProfiles
  useEffect(() => {
    if (currentJudgeProfile && currentJudgeId) {
      // console.log("Adding current judge profile to internalJudgeProfiles:", currentJudgeProfile, "for judge ID:", currentJudgeId);
      setInternalJudgeProfiles(prev => ({
        ...prev,
        [currentJudgeId]: currentJudgeProfile
      }));
    }
  }, [currentJudgeProfile, currentJudgeId]);
  

  // Calculate the maximum possible score
  const maxPossibleScore = useMemo((): number => {
    if (!criteria || !criteria.length) return 1;
    return criteria.reduce((sum, criterion) => sum + (criterion.maxPoints || 0), 0) || 1;
  }, [criteria]);

  // Fetch criteria from API
  useEffect(() => {
    const loadCriteria = async () => {
      try {
        setCriteriaLoading(true);
        const data: CriteriaApiResponse = await fetchWithAuth(CRITERIA_API);
        
        // Use prop scoring mode if provided, otherwise use API response, fallback to simple
        const effectiveScoringMode = propScoringMode || data.scoringMode || 'simple';
        console.log('🔍 LiveScoreMonitor: Scoring mode determination:', {
          propScoringMode,
          apiScoringMode: data.scoringMode,
          effectiveScoringMode
        });
        setScoringMode(effectiveScoringMode);
        
        if (effectiveScoringMode === 'simple') {
          // For simple scoring, we don't need criteria
          setCriteria([]);
        } else if (data && data.criteria && data.criteria.length > 0) {
          // Process criteria to ensure they have weight and maxPoints properties
          const processedCriteria = data.criteria.map(criterion => ({
            ...criterion,
            // Use existing weight or calculate from maxPoints
            weight: criterion.weight || 
                   (criterion.maxPoints && data.isPercentageMode ? 
                    (criterion.maxPoints / 
                     data.criteria!.reduce((sum, c) => sum + (c.maxPoints || 0), 0)) * 100 
                    : 0),
            // Ensure maxPoints exists
            maxPoints: criterion.maxPoints || 0,
            // Use either name or label property
            name: criterion.name || criterion.label || `Criterion ${criterion.id}`
          }));
          
          setCriteria(processedCriteria);
        } else {
          console.error("No criteria configured in judging and awards settings");
          console.log('🔄 LiveScoreMonitor: No criteria found, falling back to simple scoring mode');
          
          // Fallback to simple scoring mode when no criteria are configured
          setScoringMode('simple');
          setCriteria([]);
        }
      } catch (err) {
        console.error("Error loading criteria:", err);
        console.log('🔄 LiveScoreMonitor: CRITERIA_API failed, falling back to simple scoring mode');
        
        // Fallback to simple scoring mode when criteria loading fails
        setScoringMode('simple');
        setCriteria([]);
        setCriteriaError('Failed to load criteria, using simple scoring mode');
      } finally {
        setCriteriaLoading(false);
      }
    };

    loadCriteria();
  }, [propScoringMode]); // Re-run when prop scoring mode changes

  // Load awards configuration
  useEffect(() => {
    const loadAwards = async () => {
      try {
        setAwardsLoading(true);
        const data: AwardsApiResponse = await fetchWithAuth(AWARDS_API);
        setAwardsConfig({
          awards: data.awards || [],
          isPercentageBased: data.isPercentageBased ?? true,
          maxPointsPerJudge: data.maxPointsPerJudge || 100,
          numberOfJudges: data.numberOfJudges || 3
        });
      } catch (err) {
        console.error("Error loading awards configuration:", err);
        // Set default configuration if loading fails
        setAwardsConfig({
          awards: [],
          isPercentageBased: true,
          maxPointsPerJudge: 100,
          numberOfJudges: 3
        });
      } finally {
        setAwardsLoading(false);
      }
    };

    loadAwards();
  }, []);

  // Get judges and profiles from polling context
  const {
    connectedJudges: pollingConnectedJudges,
    judgeProfiles: pollingJudgeProfiles,
    judgePositions: pollingJudgePositions,
    currentRoutine: pollingCurrentRoutine,
    scheduleData,
    scoreUpdates
  } = usePolling();

  // Update internal state when polling data changes
  useEffect(() => {
    if (pollingConnectedJudges) {
      // console.log("Connected judges updated from polling:", pollingConnectedJudges);
      setConnectedJudges(pollingConnectedJudges);
    }
  }, [pollingConnectedJudges]);

  useEffect(() => {
    if (pollingJudgeProfiles) {
      // console.log("Judge profiles updated from polling:", pollingJudgeProfiles);
      setInternalJudgeProfiles(pollingJudgeProfiles as any);
    }
  }, [pollingJudgeProfiles]);

  useEffect(() => {
    if (pollingJudgePositions) {
      // console.log("Judge positions updated from polling:", pollingJudgePositions);
      setJudgePositions(pollingJudgePositions);
    }
  }, [pollingJudgePositions]);

  // Polling-based score updates - fetch scores periodically
  useEffect(() => {
    if (!connected || !competitionCode || !currentRoutine) return;

    const fetchScores = async () => {
      try {
        // Don't set loading state in polling - it causes infinite loops
        // In a real implementation, you would fetch scores from the polling API
        // For now, we'll use the existing judgeScores state
        // console.log("Fetching scores for routine:", currentRoutine.routineNumber);
        // setLastUpdateTime(new Date()); // Commented out to prevent infinite loops
      } catch (error) {
        console.error("Error fetching scores:", error);
        setError("Failed to fetch scores");
      }
    };

    // Initial fetch
    fetchScores();

    // Set up polling interval (every 3 seconds)
    const interval = setInterval(fetchScores, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [connected, competitionCode, currentRoutine]);

  // Process score updates from polling context
  useEffect(() => {
    if (!currentRoutine || !scoreUpdates) return;

    // Get the current routine ID for filtering scores - use same priority as score submission
    const currentRoutineId = currentRoutine.number || currentRoutine.id || currentRoutine.name;
    if (!currentRoutineId) return;

    // Filter and process scores for the current routine
    const routineScores: JudgeScores = {};
    
    Object.entries(scoreUpdates).forEach(([scoreKey, scoreData]) => {
      // Score key format is: routineId_judgeId
      const [routineId, ...judgeIdParts] = scoreKey.split('_');
      const judgeId = judgeIdParts.join('_'); // Rejoin in case judgeId contains underscores
      
        // Only include scores for the current routine AND that are submitted
        if (routineId === currentRoutineId.toString() && scoreData.isSubmitted) {
        // Extract simpleScore from the scores object
        const simpleScore = scoreData.scores?.simpleScore || scoreData.simpleScore;
        
        routineScores[judgeId] = {
          scores: scoreData.scores || {},
          simpleScore: simpleScore,
          comments: scoreData.comments || '',
          isSubmitted: scoreData.isSubmitted || false,
          timestamp: scoreData.timestamp,
          iChoreographedThis: scoreData.iChoreographedThis || false, // Use backend field name
          notFamilyFriendly: scoreData.notFamilyFriendly || false, // Add notFamilyFriendly flag
          styleFeedback: scoreData.styleFeedback
        };
      }
    });

    setJudgeScores(routineScores);
    // Only update timestamp if we actually have scores to prevent infinite loops
    if (Object.keys(routineScores).length > 0) {
      setLastUpdateTime(new Date());
    }
  }, [currentRoutine, scoreUpdates]);

  // Calculate score stats for each criterion or simple scores
  const calculateScoreStats = useCallback((): ScoreStats => {
  // console.log('calculateScoreStats - judgeScores:', judgeScores);
  // console.log('calculateScoreStats - scoringMode:', scoringMode);
    
    if (!judgeScores || Object.keys(judgeScores).length === 0) {
      // console.log('calculateScoreStats - No judge scores, returning empty');
      return { criteriaScores: {}, judgeCount: 0, simpleScores: {} };
    }

    const criteriaScores: CriteriaScores = {};
    const simpleScores: SimpleScores = {};
    let judgeCount = 0;

    if (scoringMode === 'simple') {
      // console.log('calculateScoreStats - Processing simple scoring mode');
      // Handle simple scoring mode
      Object.entries(judgeScores).forEach(([judgeId, judgeData]) => {
        // console.log('calculateScoreStats - Processing judge:', judgeId, judgeData);
        if (!judgeData) return;
        
        judgeCount++;
        
        // If judge choreographed this routine, handle score display appropriately
        if (judgeData.iChoreographedThis) {
          // If it's a replacement score, show the actual replacement score
          // Otherwise, show 0 and don't count toward totals
          if (judgeData.simpleScore !== undefined && judgeData.simpleScore !== null && judgeData.simpleScore > 0) {
            // This is a replacement score - show the actual score but don't count toward totals for averaging
            simpleScores[judgeId] = parseFloat(judgeData.simpleScore.toString());
          } else {
            // Original choreographer score (0) - show 0 and don't count toward totals
            simpleScores[judgeId] = 0;
          }
          return; // Don't add to totals for averaging regardless
        }
        
        // For simple scoring, look for simpleScore property
        if (judgeData.simpleScore !== undefined && judgeData.simpleScore !== null) {
          // console.log('calculateScoreStats - Found simpleScore:', judgeData.simpleScore);
          simpleScores[judgeId] = parseFloat(judgeData.simpleScore.toString());
        } else if (judgeData.scores && judgeData.scores.simpleScore !== undefined) {
          // console.log('calculateScoreStats - Found scores.simpleScore:', judgeData.scores.simpleScore);
          simpleScores[judgeId] = parseFloat(judgeData.scores.simpleScore.toString());
        } else {
          // console.log('calculateScoreStats - No simpleScore found for judge:', judgeId);
        }
      });
      
      // console.log('calculateScoreStats - Final simpleScores:', simpleScores);
      
      return { criteriaScores: {}, judgeCount, simpleScores };
    } else {
      // Handle criteria-based scoring mode
      // Initialize with zeros
      criteria.forEach((criterion) => {
        criteriaScores[criterion.id] = {
          sum: 0,
          count: 0,
          average: 0,
          total: 0,
          weight: criterion.weight || 0
        };
      });

      // Sum up scores from all judges
      Object.values(judgeScores).forEach((judgeData) => {
        if (!judgeData || !judgeData.scores) return;

        judgeCount++;
        
        // If judge choreographed this routine, don't add their scores to totals
        if (judgeData.iChoreographedThis) {
          return; // Don't add to totals
        }
        
        criteria.forEach((criterion) => {
          const score = judgeData.scores[criterion.id];
          if (score !== undefined && score !== null) {
            criteriaScores[criterion.id].sum += parseFloat(score.toString());
            criteriaScores[criterion.id].count++;
          }
        });
      });

      // Calculate averages and totals
      criteria.forEach((criterion) => {
        const { sum, count } = criteriaScores[criterion.id];
        criteriaScores[criterion.id].average = count > 0 ? sum / count : 0;
        criteriaScores[criterion.id].total = sum;
      });

      return { criteriaScores, judgeCount, simpleScores: {} };
    }
  }, [judgeScores, criteria, scoringMode]);

  // Calculate total score (sum of all criterion totals or simple scores)
  const calculateTotalScore = useCallback((): number => {
    const { criteriaScores, simpleScores } = calculateScoreStats();

    if (scoringMode === 'simple') {
      // For simple scoring, sum all judge scores
      return Object.values(simpleScores).reduce((sum, score) => sum + score, 0);
    } else {
      if (!criteriaScores || Object.keys(criteriaScores).length === 0) return 0;

      // Sum all criterion totals
      let totalScore = 0;
      Object.values(criteriaScores).forEach(data => {
        totalScore += data.total;
      });

      return totalScore;
    }
  }, [calculateScoreStats, scoringMode]);

    // Calculate average of all scores combined
  const calculateAverageScore = useCallback((): number => {
    const { criteriaScores, simpleScores, judgeCount } = calculateScoreStats();
    
    if (scoringMode === 'simple') {
      // For simple scoring, calculate average of all judge scores
      const scores = Object.values(simpleScores);
      return scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
    } else {
      if (!criteriaScores || Object.keys(criteriaScores).length === 0) return 0;

      let totalSum = 0;
      let totalCount = 0;

      Object.values(criteriaScores).forEach(data => {
        totalSum += data.sum;
        totalCount += data.count;
      });

      return totalCount > 0 ? totalSum / totalCount : 0;
    }
  }, [calculateScoreStats, scoringMode]);

  // Get count of judges who have submitted scores
  const getScoredJudgesCount = useCallback((): number => {
    if (!judgeScores) return 0;
    
    // Count judges who have scored at least one criterion
    return Object.values(judgeScores).filter(judgeData => {
      if (!judgeData || !judgeData.scores) return false;
      return Object.values(judgeData.scores).some(score => 
        score !== undefined && score !== null && score > 0
      );
    }).length;
  }, [judgeScores]);

  // Force refresh scores from server
  const refreshScores = useCallback(() => {
    if (!connected || !competitionCode || !currentRoutine) return;

    setLoading(true);
    // In a real implementation, this would make an API call to fetch scores
    console.log("Refreshing scores for routine:", currentRoutine.routineNumber);
    setLoading(false);
  }, [connected, competitionCode, currentRoutine]);

  // Handle inline score editing
  const handleScoreClick = useCallback((judgeId: string, criterionId: string) => {
    if (!enableScoreEditing) return;
    
    const currentScore = judgeScores[judgeId]?.scores?.[criterionId] || 0;
    setEditingScore({ judgeId, criterionId });
    setTempScoreValue(currentScore.toString());
  }, [enableScoreEditing, judgeScores]);

  const handleScoreBlur = useCallback(async () => {
    if (!editingScore || !onScoreUpdate) {
      setEditingScore(null);
      return;
    }

    const newScore = parseFloat(tempScoreValue);
    
    // Validation
    if (isNaN(newScore)) {
      // Invalid number, revert to original value
      setEditingScore(null);
      setTempScoreValue("");
      return;
    }

    // Get max points for validation
    let maxPoints = 100; // Default for simple scoring
    if (editingScore.criterionId !== 'simpleScore') {
      const criterion = criteria.find(c => c.id === editingScore.criterionId);
      maxPoints = criterion?.maxPoints || 10;
    }

    // Clamp score to valid range
    const clampedScore = Math.max(0, Math.min(maxPoints, newScore));
    
    if (clampedScore !== newScore) {
      // Score was clamped, show warning but still save
      console.warn(`Score ${newScore} was clamped to ${clampedScore} (max: ${maxPoints})`);
    }

    // Create unique key for this score update
    const updateKey = `${editingScore.judgeId}_${editingScore.criterionId}`;
    
    // Set loading state
    setUpdatingScores(prev => new Set([...prev, updateKey]));
    setEditingScore(null);
    setTempScoreValue("");

    try {
      await onScoreUpdate(editingScore.judgeId, editingScore.criterionId, clampedScore);
      
      // Clear loading state after a brief delay to ensure backend update is reflected
      setTimeout(() => {
        setUpdatingScores(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }, 1500); // 1.5 second delay to allow polling to pick up the change
      
    } catch (error) {
      console.error('Score update failed:', error);
      // Clear loading state immediately on error
      setUpdatingScores(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [editingScore, tempScoreValue, onScoreUpdate, criteria]);

  const handleScoreKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScoreBlur();
    } else if (e.key === 'Escape') {
      setEditingScore(null);
      setTempScoreValue("");
    }
  }, [handleScoreBlur]);

  // Handle inline comment editing
  const handleCommentClick = useCallback((judgeId: string) => {
    if (!enableScoreEditing) return;
    
    const currentComment = judgeScores[judgeId]?.comments || '';
    setEditingComment(judgeId);
    setTempCommentValue(currentComment);
  }, [enableScoreEditing, judgeScores]);

  const handleCommentBlur = useCallback(async () => {
    if (!editingComment || !onCommentUpdate) {
      setEditingComment(null);
      return;
    }

    // Trim whitespace and validate length
    const trimmedComment = tempCommentValue.trim();
    const maxCommentLength = 1000; // Match backend validation
    
    if (trimmedComment.length > maxCommentLength) {
      console.warn(`Comment length ${trimmedComment.length} exceeds maximum ${maxCommentLength}`);
      // Truncate the comment
      const truncatedComment = trimmedComment.substring(0, maxCommentLength);
      
      // Set loading state
      setUpdatingComments(prev => new Set([...prev, editingComment]));
      setEditingComment(null);
      setTempCommentValue("");

      try {
        await onCommentUpdate(editingComment, truncatedComment);
        
        // Clear loading state after a brief delay
        setTimeout(() => {
          setUpdatingComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(editingComment);
            return newSet;
          });
        }, 1500);
        
      } catch (error) {
        console.error('Comment update failed:', error);
        setUpdatingComments(prev => {
          const newSet = new Set(prev);
          newSet.delete(editingComment);
          return newSet;
        });
      }
    } else {
      // Set loading state
      setUpdatingComments(prev => new Set([...prev, editingComment]));
      setEditingComment(null);
      setTempCommentValue("");

      try {
        await onCommentUpdate(editingComment, trimmedComment);
        
        // Clear loading state after a brief delay
        setTimeout(() => {
          setUpdatingComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(editingComment);
            return newSet;
          });
        }, 1500);
        
      } catch (error) {
        console.error('Comment update failed:', error);
        setUpdatingComments(prev => {
          const newSet = new Set(prev);
          newSet.delete(editingComment);
          return newSet;
        });
      }
    }
  }, [editingComment, tempCommentValue, onCommentUpdate]);

  const handleCommentKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentBlur();
    } else if (e.key === 'Escape') {
      setEditingComment(null);
      setTempCommentValue("");
    }
  }, [handleCommentBlur]);

  // Style feedback update handler
  const handleStyleFeedbackUpdate = useCallback(async (judgeId: string, category: string, criteria: string, feedbackType: 'good' | 'needs-work' | null) => {
    if (!onStyleFeedbackUpdate) return;

    const updateKey = `${judgeId}_${category}_${criteria}`;
    
    // Set loading state
    setUpdatingStyleFeedback(prev => new Set([...prev, updateKey]));

    try {
      await onStyleFeedbackUpdate(judgeId, category, criteria, feedbackType);
      
      // Clear loading state after a brief delay
      setTimeout(() => {
        setUpdatingStyleFeedback(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }, 1500);
      
    } catch (error) {
      console.error('Style feedback update failed:', error);
      setUpdatingStyleFeedback(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [onStyleFeedbackUpdate]);

  // Track the target state for boolean flags during updates
  const [booleanFlagTargets, setBooleanFlagTargets] = useState<Map<string, boolean>>(new Map());

  // Helper function to get the display state for boolean flags (current or target during loading)
  const getBooleanFlagDisplayState = (judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', currentValue: boolean): boolean => {
    const updateKey = `${judgeId}_${flagType}`;
    if (updatingBooleanFlags.has(updateKey)) {
      // During loading, show the target state
      return booleanFlagTargets.get(updateKey) ?? currentValue;
    }
    return currentValue;
  };

  // Boolean flag update handler
  const handleBooleanFlagUpdate = useCallback(async (judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', newValue: boolean) => {
    if (!onBooleanFlagUpdate) return;

    const updateKey = `${judgeId}_${flagType}`;
    
    // Store the target state
    setBooleanFlagTargets(prev => new Map(prev).set(updateKey, newValue));
    
    // Set loading state
    setUpdatingBooleanFlags(prev => new Set([...prev, updateKey]));

    try {
      // Score zeroing is now handled in TabulatorPage's handleBooleanFlagUpdate
      // to avoid timing issues with multiple rapid API calls
      
      // If setting iChoreographedThis to false, we don't automatically restore scores
      // The tabulator will need to manually re-enter scores if needed
      
      await onBooleanFlagUpdate(judgeId, flagType, newValue);
      
      // Clear loading state after a brief delay
      setTimeout(() => {
        setUpdatingBooleanFlags(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
        setBooleanFlagTargets(prev => {
          const newMap = new Map(prev);
          newMap.delete(updateKey);
          return newMap;
        });
      }, 1500);
      
    } catch (error) {
      console.error('Boolean flag update failed:', error);
      setUpdatingBooleanFlags(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      setBooleanFlagTargets(prev => {
        const newMap = new Map(prev);
        newMap.delete(updateKey);
        return newMap;
      });
    }
  }, [onBooleanFlagUpdate, onScoreUpdate, scoringMode, criteria, updatingBooleanFlags, booleanFlagTargets]);

  // Reset score handlers
  const handleResetClick = useCallback((judgeId: string) => {
    setResetConfirmation(prev => new Set([...prev, judgeId]));
  }, []);

  const handleResetConfirm = useCallback(async (judgeId: string) => {
    if (!onScoreDelete || !currentRoutine?.id) return;

    try {
      await onScoreDelete(judgeId, currentRoutine.id);
      
      // Clear confirmation state
      setResetConfirmation(prev => {
        const newSet = new Set(prev);
        newSet.delete(judgeId);
        return newSet;
      });
    } catch (error) {
      console.error('Score deletion failed:', error);
      // Clear confirmation state even on error
      setResetConfirmation(prev => {
        const newSet = new Set(prev);
        newSet.delete(judgeId);
        return newSet;
      });
    }
  }, [onScoreDelete, currentRoutine?.id]);

  const handleResetCancel = useCallback((judgeId: string) => {
    setResetConfirmation(prev => {
      const newSet = new Set(prev);
      newSet.delete(judgeId);
      return newSet;
    });
  }, []);

  // Auto-cancel reset confirmation after 5 seconds
  useEffect(() => {
    if (resetConfirmation.size > 0) {
      const timer = setTimeout(() => {
        setResetConfirmation(new Set());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [resetConfirmation]);

  const { criteriaScores = {}, judgeCount = 0 } =
    calculateScoreStats() || {};
  const totalScore = calculateTotalScore();

  // Get all unique style feedback items across all judges (moved to component level)
  const allStyleFeedbackItems = useMemo(() => {
    const items = new Set<string>();
    
    Object.values(judgeScores || {}).forEach(judgeData => {
      if (judgeData.styleFeedback) {
        // Add good feedback items
        judgeData.styleFeedback.good?.forEach(item => {
          items.add(`${item.category}:${item.criteria}`);
        });
        
        // Add needs-work feedback items
        judgeData.styleFeedback['needs-work']?.forEach(item => {
          items.add(`${item.category}:${item.criteria}`);
        });
      }
    });
    
    return Array.from(items).sort();
  }, [judgeScores]);

  // Get feedback for a specific judge and item (moved to component level)
  const getJudgeFeedback = useCallback((judgeId: string, category: string, criteria: string): 'good' | 'needs-work' | null => {
    const judgeData = judgeScores[judgeId];
    if (!judgeData?.styleFeedback) return null;

    // Check good feedback
    const isGood = judgeData.styleFeedback.good?.some(item => 
      item.category === category && item.criteria === criteria
    );
    if (isGood) return 'good';

    // Check needs-work feedback
    const isNeedsWork = judgeData.styleFeedback['needs-work']?.some(item => 
      item.category === category && item.criteria === criteria
    );
    if (isNeedsWork) return 'needs-work';

    return null;
  }, [judgeScores]);

  // Create judge style feedback object for modal (moved to component level)
  const judgeStyleFeedbackForModal = useMemo(() => {
    const feedback: Record<string, any> = {};
    Object.entries(judgeScores || {}).forEach(([judgeId, judgeData]) => {
      if (judgeData.styleFeedback) {
        feedback[judgeId] = judgeData.styleFeedback;
      }
    });
    return feedback;
  }, [judgeScores]);

  // Get score color based on percentage - use single color
  const getScoreColor = (percentage: number): string => {
    return "text-blue-500 dark:text-blue-400";
  };

  // Get score background color based on percentage - use single color
  const getScoreBgColor = (percentage: number): string => {
    return "bg-blue-100 dark:bg-blue-900/30";
  };

  // Get award for a combined total score using dynamic configuration
  const getAward = (score: number): Award | null => {
    if (score === null || score === undefined || !awardsConfig?.awards?.length) return null;
    
    const { awards } = awardsConfig;
    
    const award = awards.find(
      award => score >= award.minScore && score <= award.maxScore
    );
    
    return award || awards[awards.length - 1]; // Default to lowest award
  };
  
  // Get award for a single judge's percentage using dynamic configuration
  const getJudgeAward = (percentage: number): Award | null => {
    if (percentage === null || percentage === undefined || !awardsConfig?.awards?.length) return null;
    
    const { awards, numberOfJudges } = awardsConfig;
    
    // Scale the award ranges for individual scoring (single judge)
    const scaledAwards = awards.map((award) => ({
      ...award,
      scaledMin: award.minScore / numberOfJudges,
      scaledMax: award.maxScore / numberOfJudges,
    }));
    
    const award = scaledAwards.find(
      award => percentage >= award.scaledMin! && percentage <= award.scaledMax!
    );
    
    return award || scaledAwards[scaledAwards.length - 1]; // Default to lowest award
  };

  // Render scores tab - condensed version
  const renderScoresTab = (): React.JSX.Element => {
    const judgeCount = Array.isArray(sortedConnectedJudges) ? sortedConnectedJudges.length : 0;
    const hasScores = Object.keys(judgeScores || {}).length > 0;
    const { criteriaScores, simpleScores } = calculateScoreStats();
    
    if (scoringMode === 'simple') {
      // SIMPLE scoring layout rendering - logging removed to reduce console spam
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex-grow overflow-hidden">
            <div className={`${sizeConfig.padding} h-full overflow-y-auto`}>
              {/* Simple scores display - Column Layout */}
              <div className="flex flex-col">
            {/* Judge Row with inline title */}
            <div className="flex items-center mb-4 pb-2 border-b border-gray-200 dark:border-gray-700/30">
              {/* Judge Title */}
              <div className="w-20 flex-shrink-0">
                <div className={`${sizeConfig.categorySize} font-medium text-gray-400 dark:text-gray-500`}>
                  Judge
                </div>
              </div>
              
              {/* Judge Photos and Names */}
              <div className="flex flex-1">
                {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.map((judgeId, index) => {
                  const judgeProfile = combinedJudgeProfiles[judgeId] || judgeId;
                  
                  return (
                    <div key={`header-${judgeId}`} 
                      className={`flex-1 flex flex-col items-center ${index === sortedConnectedJudges.length - 1 ? "mr-0" : isCompactMode ? "mr-1" : "mr-2"}`}>
                      {/* Judge Avatar */}
                      <div className="mb-1">
                        {getProfileAvatar(judgeProfile, sizeConfig.avatarSize)}
                      </div>
                      
                      {/* Judge Name with Position and TC indicator */}
                      <div className={`${sizeConfig.categorySize} font-medium text-center truncate w-full px-1 flex items-center justify-center gap-1`}>
                        {/* Judge Position - subtle */}
                        {judgePositions[judgeId] && (
                          <span className="flex-shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
                            #{judgePositions[judgeId]}
                          </span>
                        )}
                        <span className="truncate">
                          {getJudgeFirstName(judgeProfile)}
                        </span>
                        {typeof judgeProfile === 'object' && judgeProfile.isTeacherCritique && (
                          <span className="flex-shrink-0 px-1 py-0.5 text-[10px] font-bold bg-orange-500/20 text-orange-300 rounded-full border border-orange-400/30">
                            TC
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Score Row with inline title */}
            <div className="flex mb-4">
              {/* Score Title - aligned with score display area */}
              <div className="w-20 flex-shrink-0 flex flex-col">
                <div className="min-h-[2.5rem] flex items-center justify-start">
                  <div className={`${sizeConfig.categorySize} font-medium text-gray-400 dark:text-gray-500`}>
                    Score
                  </div>
                </div>
                {/* Spacer to align with icon area */}
                <div className="min-h-[1.5rem] mt-1"></div>
              </div>
              
              {/* Scores */}
              <div className="flex flex-1">
                {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.map((judgeId, index) => {
                  const judgeData = judgeScores[judgeId];
                  const score = simpleScores[judgeId] || 0;
                  const scorePercentage = (score / 100) * 100; // Simple score is out of 100
                  
                  return (
                    <div key={`score-${judgeId}`} 
                      className={`flex-1 flex justify-center items-center ${index === sortedConnectedJudges.length - 1 ? "mr-0" : isCompactMode ? "mr-1" : "mr-2"}`}>
                      {/* Inline editable score */}
                      {enableScoreEditing && editingScore?.judgeId === judgeId && editingScore?.criterionId === 'simpleScore' ? (
                        <input
                          type="number"
                          value={tempScoreValue}
                          onChange={(e) => setTempScoreValue(e.target.value)}
                          onBlur={handleScoreBlur}
                          onKeyDown={handleScoreKeyDown}
                          className={`w-16 text-center ${sizeConfig.scoreSize} font-semibold bg-transparent border border-blue-500 rounded px-1 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}
                          autoFocus
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      ) : updatingScores.has(`${judgeId}_simpleScore`) ? (
                        // Loading state with pulsing animation
                        <div className={`${sizeConfig.scoreSize} font-semibold flex items-center justify-center`}>
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing"></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          {/* Score display - fixed height to maintain alignment */}
                          <div className="flex flex-col items-center min-h-[2.5rem] justify-center">
                            <div 
                              className={`${sizeConfig.scoreSize} font-semibold ${getScoreColor(scorePercentage)} ${judgeData?.iChoreographedThis ? 'opacity-50' : ''} ${
                                enableScoreEditing ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors' : ''
                              }`}
                              onClick={() => enableScoreEditing && handleScoreClick(judgeId, 'simpleScore')}
                              title={enableScoreEditing ? 'Click to edit score' : ''}
                            >
                              {formatScore(score)}
                            </div>
                          </div>
                          
                          {/* Boolean flag toggles - dedicated space below score */}
                          <div className="flex gap-1 mt-1 min-h-[1.5rem] items-center">
                            {enableScoreEditing ? (
                              <>
                                {/* Choreographer toggle */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    getBooleanFlagDisplayState(judgeId, 'iChoreographedThis', judgeData?.iChoreographedThis || false)
                                      ? 'bg-purple-500/30 text-purple-200 border-purple-400/50 hover:bg-purple-500/40' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                  } ${updatingBooleanFlags.has(`${judgeId}_iChoreographedThis`) ? 'animate-breathing' : ''}`}
                                  onClick={() => handleBooleanFlagUpdate(judgeId, 'iChoreographedThis', !judgeData?.iChoreographedThis)}
                                  title={judgeData?.iChoreographedThis ? 'Judge choreographed this routine - Click to unmark' : 'Click to mark as choreographed by judge'}
                                >
                                  <UserCheck size={12} />
                                </div>
                                
                                {/* Replace with Average button - only show for choreographer judges */}
                                {judgeData?.iChoreographedThis && onChoreographerScoreReplacement && (
                                  <div 
                                    className="p-1 rounded border cursor-pointer transition-all duration-200 bg-blue-500/30 text-blue-200 border-blue-400/50 hover:bg-blue-500/40"
                                    onClick={() => onChoreographerScoreReplacement(judgeId)}
                                    title="Replace choreographer's score with average of other judges"
                                  >
                                    <Calculator size={12} />
                                  </div>
                                )}
                                
                                {/* Not Family Friendly toggle */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    getBooleanFlagDisplayState(judgeId, 'notFamilyFriendly', judgeData?.notFamilyFriendly || false)
                                      ? 'bg-orange-500/30 text-orange-200 border-orange-400/50 hover:bg-orange-500/40' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                  } ${updatingBooleanFlags.has(`${judgeId}_notFamilyFriendly`) ? 'animate-breathing' : ''}`}
                                  onClick={() => handleBooleanFlagUpdate(judgeId, 'notFamilyFriendly', !judgeData?.notFamilyFriendly)}
                                  title={judgeData?.notFamilyFriendly ? 'Marked as not family friendly - Click to unmark' : 'Click to mark as not family friendly'}
                                >
                                  <ShieldAlert size={12} />
                                </div>
                                
                                {/* Reset/Delete Score Button */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    resetConfirmation.has(judgeId)
                                      ? 'bg-red-500/30 text-red-200 border-red-400/50 hover:bg-red-500/40 animate-breathing' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30'
                                  }`}
                                  onClick={() => resetConfirmation.has(judgeId) ? handleResetConfirm(judgeId) : handleResetClick(judgeId)}
                                  title={resetConfirmation.has(judgeId) ? 'Click to confirm deletion - This will permanently remove this judge\'s score' : 'Reset score - Click to delete this judge\'s entire score entry'}
                                >
                                  {resetConfirmation.has(judgeId) ? <AlertTriangle size={12} /> : <RotateCcw size={12} />}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Read-only indicators when editing is disabled */}
                                {judgeData?.iChoreographedThis && (
                                  <div 
                                    className="p-1 bg-purple-500/20 text-purple-300 rounded border border-purple-400/30"
                                    title="Judge choreographed this routine"
                                  >
                                    <UserCheck size={12} />
                                  </div>
                                )}
                                {judgeData?.notFamilyFriendly && (
                                  <div 
                                    className="p-1 bg-orange-500/20 text-orange-300 rounded border border-orange-400/30"
                                    title="Marked as not family friendly"
                                  >
                                    <ShieldAlert size={12} />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // CRITERIA-BASED scoring layout rendering - logging removed to reduce console spam
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-grow overflow-hidden">
          <div className={`${sizeConfig.padding} h-full overflow-y-auto`}>
        {/* Header row with category and judges */}
        <div className="flex mb-2 flex-shrink-0">
          {/* Category header - fixed width */}
          <div className="w-1/4 flex items-center">
            <div className={`${sizeConfig.categorySize} font-medium text-gray-400 dark:text-gray-500`}>
              Category
            </div>
          </div>
          
          {/* Judges section - takes remaining width */}
          <div className="w-3/4 flex">
            {/* Judge columns with even spacing */}
            {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.map((judgeId, index) => {
              const judgeProfile = combinedJudgeProfiles[judgeId] || judgeId;
              
              const judgeData = typeof judgeProfile === 'string' ? { id: judgeProfile } : judgeProfile;
              
              return (
                <div key={judgeId} 
                  className={`flex-1 flex flex-col items-center ${index === sortedConnectedJudges.length - 1 ? "mr-0" : isCompactMode ? "mr-1" : "mr-3"}`}>
                  <div className="flex items-center gap-1">
                    {getProfileAvatar(judgeProfile, sizeConfig.avatarSize)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Criteria rows container */}
        <div className="flex-grow flex flex-col justify-between">
          {/* Criteria rows */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700/30 mb-2">
            {/* If we have scores or we're showing the template, display criteria rows */}
            {hasScores ? (
              /* Criteria rows with actual scores */
              criteria.map((criterion, index) => (
                <div key={criterion.id} className={`flex ${isCompactMode ? 'py-1.5' : 'py-3'}`}>
                  {/* Category name and weight - fixed width */}
                  <div className="w-1/4 pr-2">
                    <div className={`${sizeConfig.categorySize} font-medium truncate`}>
                      <span className="truncate">{criterion.name}</span>
                      <span className={`ml-1 ${sizeConfig.tabTextSize} text-gray-500 dark:text-gray-400 flex-shrink-0`}>
                        {criterion.weight}%
                      </span>
                    </div>
                  </div>
                  
                  {/* Scores section - takes remaining width */}
                  <div className="w-3/4 flex">
                    {/* Judge scores with even spacing */}
                    {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.map((judgeId, index) => {
                      const judgeData = judgeScores[judgeId];
                      const score = judgeData?.scores?.[criterion.id];
                      const maxPoints = criterion.maxPoints || 10;
                      
                      // If judge choreographed this routine, show replacement score if available, otherwise 0
                      let displayScore = score;
                      if (judgeData?.iChoreographedThis) {
                        // If it's a replacement score, show the actual score, otherwise show 0
                        displayScore = (score !== undefined && score !== null && score > 0) ? score : 0;
                      }
                      const scorePercentage = displayScore !== undefined ? (parseFloat(displayScore.toString()) / maxPoints) * 100 : 0;
                      
                      return (
                        <div key={`${judgeId}-${criterion.id}`} 
                          className={`flex-1 flex justify-center ${index === sortedConnectedJudges.length - 1 ? "mr-0" : isCompactMode ? "mr-1" : "mr-3"}`}>
                          {/* Inline editable score */}
                          {enableScoreEditing && editingScore?.judgeId === judgeId && editingScore?.criterionId === criterion.id ? (
                            <input
                              type="number"
                              value={tempScoreValue}
                              onChange={(e) => setTempScoreValue(e.target.value)}
                              onBlur={handleScoreBlur}
                              onKeyDown={handleScoreKeyDown}
                              className={`w-16 text-center ${sizeConfig.scoreSize} font-semibold bg-transparent border border-blue-500 rounded px-1 ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                              }`}
                              autoFocus
                              min="0"
                              max={maxPoints}
                              step="0.1"
                            />
                          ) : updatingScores.has(`${judgeId}_${criterion.id}`) ? (
                            // Loading state with pulsing animation
                            <div className={`${sizeConfig.scoreSize} font-semibold flex items-center justify-center`}>
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing"></div>
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              {/* Score display - fixed height to maintain alignment */}
                              <div className="flex flex-col items-center min-h-[2.5rem] justify-center">
                                <div 
                                  className={`${sizeConfig.scoreSize} font-semibold ${getScoreColor(scorePercentage)} ${judgeData?.iChoreographedThis ? 'opacity-50' : ''} ${
                                    enableScoreEditing && !judgeData?.iChoreographedThis ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors' : ''
                                  }`}
                                  onClick={() => enableScoreEditing && !judgeData?.iChoreographedThis && handleScoreClick(judgeId, criterion.id)}
                                  title={enableScoreEditing && !judgeData?.iChoreographedThis ? 'Click to edit score' : ''}
                                >
                                  {displayScore !== undefined ? formatScore(parseFloat(displayScore.toString())) : "0"}
                                </div>
                              </div>
                              
                              {/* Boolean flag toggles - only show on first criterion, dedicated space below score */}
                              {index === 0 && (
                                <div className="flex gap-1 mt-1 min-h-[1.5rem] items-center">
                                  {enableScoreEditing ? (
                                    <>
                                      {/* Choreographer toggle */}
                                      <div 
                                        className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                          getBooleanFlagDisplayState(judgeId, 'iChoreographedThis', judgeData?.iChoreographedThis || false)
                                            ? 'bg-purple-500/30 text-purple-200 border-purple-400/50 hover:bg-purple-500/40' 
                                            : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                        } ${updatingBooleanFlags.has(`${judgeId}_iChoreographedThis`) ? 'animate-breathing' : ''}`}
                                        onClick={() => handleBooleanFlagUpdate(judgeId, 'iChoreographedThis', !judgeData?.iChoreographedThis)}
                                        title={judgeData?.iChoreographedThis ? 'Judge choreographed this routine - Click to unmark' : 'Click to mark as choreographed by judge'}
                                      >
                                        <UserCheck size={12} />
                                      </div>
                                      
                                      {/* Replace with Average button - only show for choreographer judges */}
                                      {judgeData?.iChoreographedThis && onChoreographerScoreReplacement && (
                                        <div 
                                          className="p-1 rounded border cursor-pointer transition-all duration-200 bg-blue-500/30 text-blue-200 border-blue-400/50 hover:bg-blue-500/40"
                                          onClick={() => onChoreographerScoreReplacement(judgeId)}
                                          title="Replace choreographer's score with average of other judges"
                                        >
                                          <Calculator size={12} />
                                        </div>
                                      )}
                                      
                                      {/* Not Family Friendly toggle */}
                                      <div 
                                        className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                          getBooleanFlagDisplayState(judgeId, 'notFamilyFriendly', judgeData?.notFamilyFriendly || false)
                                            ? 'bg-orange-500/30 text-orange-200 border-orange-400/50 hover:bg-orange-500/40' 
                                            : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                        } ${updatingBooleanFlags.has(`${judgeId}_notFamilyFriendly`) ? 'animate-breathing' : ''}`}
                                        onClick={() => handleBooleanFlagUpdate(judgeId, 'notFamilyFriendly', !judgeData?.notFamilyFriendly)}
                                        title={judgeData?.notFamilyFriendly ? 'Marked as not family friendly - Click to unmark' : 'Click to mark as not family friendly'}
                                      >
                                        <ShieldAlert size={12} />
                                      </div>
                                      
                                      {/* Reset/Delete Score Button */}
                                      <div 
                                        className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                          resetConfirmation.has(judgeId)
                                            ? 'bg-red-500/30 text-red-200 border-red-400/50 hover:bg-red-500/40 animate-breathing' 
                                            : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30'
                                        }`}
                                        onClick={() => resetConfirmation.has(judgeId) ? handleResetConfirm(judgeId) : handleResetClick(judgeId)}
                                        title={resetConfirmation.has(judgeId) ? 'Click to confirm deletion - This will permanently remove this judge\'s score' : 'Reset score - Click to delete this judge\'s entire score entry'}
                                      >
                                        {resetConfirmation.has(judgeId) ? <AlertTriangle size={12} /> : <RotateCcw size={12} />}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      {/* Read-only indicators when editing is disabled */}
                                      {judgeData?.iChoreographedThis && (
                                        <div 
                                          className="p-1 bg-purple-500/20 text-purple-300 rounded border border-purple-400/30"
                                          title="Judge choreographed this routine"
                                        >
                                          <UserCheck size={12} />
                                        </div>
                                      )}
                                      {judgeData?.notFamilyFriendly && (
                                        <div 
                                          className="p-1 bg-orange-500/20 text-orange-300 rounded border border-orange-400/30"
                                          title="Marked as not family friendly"
                                        >
                                          <ShieldAlert size={12} />
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                              
                              {/* For non-first criteria, maintain consistent spacing */}
                              {index !== 0 && (
                                <div className="min-h-[1.5rem] mt-1"></div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              /* Template criteria rows when no scores */
              criteria.map((criterion, index) => (
                <div key={criterion.id} className={`flex ${isCompactMode ? 'py-1.5' : 'py-3'}`}>
                  {/* Category name and weight - fixed width */}
                  <div className="w-1/4 pr-2">
                    <div className={`${sizeConfig.categorySize} font-medium truncate`}>
                      <span className="truncate">{criterion.name}</span>
                      <span className={`ml-1 ${sizeConfig.tabTextSize} text-gray-500 dark:text-gray-400 flex-shrink-0`}>
                        {criterion.weight}%
                      </span>
                    </div>
                  </div>
                  
                  {/* Empty score placeholders */}
                  <div className="w-3/4 flex">
                    {/* Judge score placeholders */}
                    {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.map((judgeId, index) => (
                      <div key={`${judgeId}-${criterion.id}`} 
                        className={`flex-1 flex justify-center ${index === sortedConnectedJudges.length - 1 ? "mr-0" : isCompactMode ? "mr-1" : "mr-3"}`}>
                        <div className={`${sizeConfig.scoreSize} font-semibold text-gray-500 dark:text-gray-600`}>
                          0
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  };

  // Render comments tab - condensed version
  const renderCommentsTab = (): React.JSX.Element => {
    const hasScores = Object.keys(judgeScores || {}).length > 0;
    
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-grow overflow-hidden">
          {Array.isArray(sortedConnectedJudges) && sortedConnectedJudges.length > 0 ? (
            <div className={`${sizeConfig.padding} h-full overflow-y-auto`}>
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${sizeConfig.gridGap}`}>
                {sortedConnectedJudges.map((judgeId) => {
                const judgeScoreData = judgeScores[judgeId];
                const comments = judgeScoreData?.comments;
                const isSubmitted = judgeScoreData?.isSubmitted;
                const judgeProfile = combinedJudgeProfiles[judgeId] || judgeId;
                // console.log(`Rendering comments for judge ${judgeId}:`, judgeProfile);
                
                // Calculate judge's total score
                let totalScore = 0;
                let totalMaxPoints = 0;
                let scoreCount = 0;

                if (judgeScoreData?.scores) {
                  criteria.forEach(criterion => {
                    const score = judgeScoreData.scores[criterion.id];
                    if (score !== undefined && score !== null) {
                      totalScore += parseFloat(score.toString());
                      totalMaxPoints += (criterion.maxPoints || 10);
                      scoreCount++;
                    }
                  });
                }

                const judgeTotal = scoreCount > 0 ? totalScore : 0;
                const judgePercentage = totalMaxPoints > 0 ? (totalScore / totalMaxPoints) * 100 : 0;

                return (
                  <div
                    key={`${judgeId}-comments`}
                    className={`${sizeConfig.cardPadding} rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-all duration-200 flex flex-col`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {getProfileAvatar(judgeProfile, sizeConfig.avatarSize)}
                        <div className="flex-1 min-w-0">
                          <div className={`${sizeConfig.categorySize} font-medium text-gray-900 dark:text-white truncate`}>
                            {getJudgeFirstName(judgeProfile)}
                          </div>
                          <div className={`${sizeConfig.tabTextSize} text-gray-600 dark:text-gray-400`}>
                            Judge {sortedConnectedJudges.indexOf(judgeId) + 1}
                          </div>
                        </div>
                      </div>

                      {scoreCount > 0 && (
                        <div className="flex flex-col items-center">
                          {/* Score display - fixed height to maintain alignment */}
                          <div className="flex flex-col items-center min-h-[2.5rem] justify-center">
                            <div className={`${sizeConfig.scoreSize} font-bold ${getScoreColor(judgePercentage)} ${judgeScoreData?.iChoreographedThis ? 'opacity-50' : ''}`}>
                              {formatScore(judgeTotal)}
                            </div>
                          </div>
                          
                          {/* Boolean flag toggles - dedicated space below score */}
                          <div className="flex gap-1 mt-1 min-h-[1.5rem] items-center">
                            {enableScoreEditing ? (
                              <>
                                {/* Choreographer toggle */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    getBooleanFlagDisplayState(judgeId, 'iChoreographedThis', judgeScoreData?.iChoreographedThis || false)
                                      ? 'bg-purple-500/30 text-purple-200 border-purple-400/50 hover:bg-purple-500/40' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                  } ${updatingBooleanFlags.has(`${judgeId}_iChoreographedThis`) ? 'animate-breathing' : ''}`}
                                  onClick={() => handleBooleanFlagUpdate(judgeId, 'iChoreographedThis', !judgeScoreData?.iChoreographedThis)}
                                  title={judgeScoreData?.iChoreographedThis ? 'Judge choreographed this routine - Click to unmark' : 'Click to mark as choreographed by judge'}
                                >
                                  <UserCheck size={12} />
                                </div>
                                
                                {/* Not Family Friendly toggle */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    getBooleanFlagDisplayState(judgeId, 'notFamilyFriendly', judgeScoreData?.notFamilyFriendly || false)
                                      ? 'bg-orange-500/30 text-orange-200 border-orange-400/50 hover:bg-orange-500/40' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-gray-500/30'
                                  } ${updatingBooleanFlags.has(`${judgeId}_notFamilyFriendly`) ? 'animate-breathing' : ''}`}
                                  onClick={() => handleBooleanFlagUpdate(judgeId, 'notFamilyFriendly', !judgeScoreData?.notFamilyFriendly)}
                                  title={judgeScoreData?.notFamilyFriendly ? 'Marked as not family friendly - Click to unmark' : 'Click to mark as not family friendly'}
                                >
                                  <ShieldAlert size={12} />
                                </div>
                                
                                {/* Reset/Delete Score Button */}
                                <div 
                                  className={`p-1 rounded border cursor-pointer transition-all duration-200 ${
                                    resetConfirmation.has(judgeId)
                                      ? 'bg-red-500/30 text-red-200 border-red-400/50 hover:bg-red-500/40 animate-breathing' 
                                      : 'bg-gray-500/20 text-gray-400 border-gray-600/30 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30'
                                  }`}
                                  onClick={() => resetConfirmation.has(judgeId) ? handleResetConfirm(judgeId) : handleResetClick(judgeId)}
                                  title={resetConfirmation.has(judgeId) ? 'Click to confirm deletion - This will permanently remove this judge\'s score' : 'Reset score - Click to delete this judge\'s entire score entry'}
                                >
                                  {resetConfirmation.has(judgeId) ? <AlertTriangle size={12} /> : <RotateCcw size={12} />}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Read-only indicators when editing is disabled */}
                                {judgeScoreData?.iChoreographedThis && (
                                  <div 
                                    className="p-1 bg-purple-500/20 text-purple-300 rounded border border-purple-400/30"
                                    title="Judge choreographed this routine"
                                  >
                                    <UserCheck size={12} />
                                  </div>
                                )}
                                {judgeScoreData?.notFamilyFriendly && (
                                  <div 
                                    className="p-1 bg-orange-500/20 text-orange-300 rounded border border-orange-400/30"
                                    title="Marked as not family friendly"
                                  >
                                    <ShieldAlert size={12} />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={`${sizeConfig.cardPadding} rounded-lg bg-gray-200 dark:bg-gray-900/70 border border-gray-300 dark:border-gray-800 ${sizeConfig.tabTextSize} flex-grow overflow-hidden`}>
                      {/* Inline editable comments */}
                      {enableScoreEditing && editingComment === judgeId ? (
                        <textarea
                          value={tempCommentValue}
                          onChange={(e) => setTempCommentValue(e.target.value)}
                          onBlur={handleCommentBlur}
                          onKeyDown={handleCommentKeyDown}
                          className={`w-full h-full resize-none bg-transparent border border-blue-500 rounded p-2 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          } focus:outline-none`}
                          placeholder="Enter comments..."
                          autoFocus
                        />
                      ) : updatingComments.has(judgeId) ? (
                        // Loading state for comments
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing"></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-breathing" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className={`w-full h-full ${enableScoreEditing ? 'cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-800 rounded transition-colors' : ''} ${
                            comments ? '' : 'flex items-center justify-center'
                          }`}
                          onClick={() => enableScoreEditing && handleCommentClick(judgeId)}
                          title={enableScoreEditing ? 'Click to edit comments' : ''}
                        >
                      {comments ? (
                        <p className="line-clamp-4">{comments}</p>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 italic text-center">
                              {enableScoreEditing ? "Click to add comments..." : (hasScores && judgeScoreData ? "No comments provided" : "Waiting for comments...")}
                        </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center text-base text-gray-500 dark:text-gray-400">
              No judge comments available
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to get feedback counts from either format
  const getFeedbackCounts = (styleFeedback: any): { goodCount: number, needsWorkCount: number } => {
    if (!styleFeedback || typeof styleFeedback !== 'object') {
      return { goodCount: 0, needsWorkCount: 0 };
    }

    // Handle the DynamoDB format first
    if (styleFeedback.good && styleFeedback.good.L && Array.isArray(styleFeedback.good.L) && 
        styleFeedback['needs-work'] && styleFeedback['needs-work'].L && Array.isArray(styleFeedback['needs-work'].L)) {
      return {
        goodCount: styleFeedback.good.L.length,
        needsWorkCount: styleFeedback['needs-work'].L.length
      };
    }

    // Handle partial DynamoDB format (might have only one array)
    let goodCount = 0;
    let needsWorkCount = 0;
    
    if (styleFeedback.good && styleFeedback.good.L && Array.isArray(styleFeedback.good.L)) {
      goodCount = styleFeedback.good.L.length;
    }
    
    if (styleFeedback['needs-work'] && styleFeedback['needs-work'].L && Array.isArray(styleFeedback['needs-work'].L)) {
      needsWorkCount = styleFeedback['needs-work'].L.length;
    }

    // If we got counts from DynamoDB format, return them
    if (goodCount > 0 || needsWorkCount > 0) {
      return { goodCount, needsWorkCount };
    }

    // Check if this is the old individual format
    const isIndividualFormat = Object.values(styleFeedback).some(value => 
      value === 'good' || value === 'needs-work'
    );

    if (isIndividualFormat) {
      const goodCount = Object.values(styleFeedback).filter(value => value === 'good').length;
      const needsWorkCount = Object.values(styleFeedback).filter(value => value === 'needs-work').length;
      return { goodCount, needsWorkCount };
    }

    // Handle the simplified grouped format (for backward compatibility)
    goodCount = styleFeedback.good?.length || 0;
    needsWorkCount = styleFeedback['needs-work']?.length || 0;
    
    return { goodCount, needsWorkCount };
  };

  // Render style feedback tab - shows button to open modal
  const renderStyleFeedbackTab = (): React.JSX.Element => {
    // Use sortedConnectedJudges instead of Object.keys(judgeScores) to exclude system entries like flexible_scoring
    const judgeIds = Array.isArray(sortedConnectedJudges) ? sortedConnectedJudges : [];
    
    if (judgeIds.length === 0) {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex-grow overflow-hidden">
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Sparkles className="mx-auto mb-4 text-gray-400 opacity-50" size={48} />
                <p className={`${sizeConfig.tabTextSize} text-gray-500 dark:text-gray-400`}>
                  No judges found for this routine
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-grow overflow-hidden">
          <div className={`${sizeConfig.padding} h-full overflow-y-auto`}>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${sizeConfig.gridGap}`}>
              {judgeIds.map((judgeId) => {
                const judgeProfile = combinedJudgeProfiles[judgeId];
                const judgeData = judgeScores[judgeId];
                
                // Count good and needs work feedback for this judge using helper function
                const { goodCount, needsWorkCount } = getFeedbackCounts(judgeData?.styleFeedback);
                const totalCount = goodCount + needsWorkCount;
                
                return (
                  <div
                    key={judgeId}
                    className={`${sizeConfig.cardPadding} rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-all duration-200`}
                  >
                    {/* Judge Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Judge Photo */}
                        {typeof judgeProfile === 'object' && judgeProfile.photoUrl ? (
                          <img 
                            src={judgeProfile.photoUrl} 
                            alt={judgeProfile.name || judgeId}
                            className={`${sizeConfig.avatarSize} rounded-full object-cover border-2 border-white/20 dark:border-gray-600`}
                          />
                        ) : (
                          <div className={`${sizeConfig.avatarSize} rounded-full bg-gray-600 flex items-center justify-center`}>
                            <User className="w-5 h-5 text-white/70" />
                          </div>
                        )}
                        
                        {/* Judge Info */}
                        <div className="flex-1 min-w-0">
                          <div className={`${sizeConfig.categorySize} font-medium text-gray-900 dark:text-white truncate`}>
                            {getJudgeFirstName(judgeProfile)}
                          </div>
                          <div className={`${sizeConfig.tabTextSize} text-gray-600 dark:text-gray-400`}>
                            Judge {judgeIds.indexOf(judgeId) + 1}
                          </div>
                        </div>
                      </div>
                      
                      {/* Edit Button */}
                      <button
                        onClick={() => {
                          const judgeIndex = judgeIds.indexOf(judgeId);
                          setInitialJudgeIndex(judgeIndex);
                          setIsStyleFeedbackModalOpen(true);
                        }}
                        disabled={!enableScoreEditing}
                        className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 hover:text-purple-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit feedback for this judge"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                
                    {/* Feedback Summary */}
                    <div className="space-y-2">
                      {totalCount === 0 ? (
                        <div className={`${sizeConfig.tabTextSize} text-gray-500 dark:text-gray-500 text-center py-2 text-sm`}>
                          No feedback submitted
                        </div>
                      ) : (
                        <>
                          {/* Good Feedback */}
                          {goodCount > 0 && (
                            <div className="flex items-center justify-between p-2 bg-green-500/10 border border-green-400/30 rounded-md">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span className={`${sizeConfig.tabTextSize} text-green-300 text-sm`}>Good</span>
                              </div>
                              <span className={`${sizeConfig.tabTextSize} font-medium text-green-200 text-sm`}>{goodCount}</span>
                            </div>
                          )}
                          
                          {/* Needs Work Feedback */}
                          {needsWorkCount > 0 && (
                            <div className="flex items-center justify-between p-2 bg-orange-500/10 border border-orange-400/30 rounded-md">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                <span className={`${sizeConfig.tabTextSize} text-orange-300 text-sm`}>Needs Work</span>
                              </div>
                              <span className={`${sizeConfig.tabTextSize} font-medium text-orange-200 text-sm`}>{needsWorkCount}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={containerRef} 
      className={`flex flex-col h-full ${className}`}
      style={{
        maxHeight: maxHeight || 'calc(100vh - 100px)',
        minHeight: '200px'
      }}
    >
      {/* Inject custom breathing animation styles */}
      <style dangerouslySetInnerHTML={{ __html: breathingStyles }} />
      <div className="flex justify-between items-center mb-2 flex-shrink-0">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <CardTitle className={`flex items-center ${universalConfig.spacing}`}>
            <Zap className={`${universalConfig.iconSize} text-blue-400`} />
            LIVE SCORES
          </CardTitle>
        </motion.div>
        
        {/* Container-responsive tabs for navigation */}
        <div className={`flex ${sizeConfig.gridGap}`}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('scores')}
            className={`${sizeConfig.tabPadding} ${sizeConfig.tabTextSize} rounded-full font-semibold transition-all backdrop-blur-xl ${
              activeTab === 'scores'
                ? 'bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-400/50 text-blue-200'
                : 'bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20'
            }`}
          >
            <div className="flex items-center gap-1">
              <Target size={sizeConfig.tabIconSize} />
              Scores
            </div>
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('comments')}
            className={`${sizeConfig.tabPadding} ${sizeConfig.tabTextSize} rounded-full font-semibold transition-all backdrop-blur-xl ${
              activeTab === 'comments'
                ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-purple-200'
                : 'bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20'
            }`}
          >
            <div className="flex items-center gap-1">
              <MessageCircle size={sizeConfig.tabIconSize} />
              Comments
            </div>
          </motion.button>
          {finalizedScore && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab('final')}
              className={`${sizeConfig.tabPadding} ${sizeConfig.tabTextSize} rounded-full font-semibold transition-all backdrop-blur-xl ${
                activeTab === 'final'
                  ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200'
                  : 'bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20'
              }`}
            >
              <div className="flex items-center gap-1">
                <Calculator size={sizeConfig.tabIconSize} />
                Final
              </div>
            </motion.button>
          )}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('stats')}
            className={`${sizeConfig.tabPadding} ${sizeConfig.tabTextSize} rounded-full font-semibold transition-all backdrop-blur-xl ${
              activeTab === 'stats'
                ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-purple-200'
                : 'bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20'
            }`}
          >
            <div className="flex items-center gap-1">
              <Sparkles size={sizeConfig.tabIconSize} />
              Style Feedback
            </div>
          </motion.button>
        </div>
      </div>
      
      <div className="flex-grow overflow-hidden">
        {activeTab === 'scores' && renderScoresTab()}
        {activeTab === 'comments' && renderCommentsTab()}
        {activeTab === 'stats' && renderStyleFeedbackTab()}
        {activeTab === 'final' && finalizedScore && (
          <div className="h-full p-4">
            <FlexibleScoreDisplay 
              scoreData={finalizedScore}
              routine={currentRoutine}
              showDetails={true}
              onToggleDetails={() => {}}
            />
          </div>
        )}
      </div>

      {/* Style Feedback Editor Modal */}
      <StyleFeedbackEditorModal
        isOpen={isStyleFeedbackModalOpen}
        onClose={() => setIsStyleFeedbackModalOpen(false)}
        currentRoutine={currentRoutine}
        judgeIds={Array.isArray(sortedConnectedJudges) ? sortedConnectedJudges : []}
        judgeProfiles={combinedJudgeProfiles}
        judgeStyleFeedback={judgeStyleFeedbackForModal}
        styleScoreSheets={styleScoreSheets}
        onFeedbackUpdate={onStyleFeedbackUpdate || (() => {})}
        initialJudgeIndex={initialJudgeIndex}
      />

    </div>
  );
};

export default memo(LiveScoreMonitor);
